<template>
  <div>
    <PageAllow controller="PurchaseTasks" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRange
              type="datetime"
              v-model="datetimeRage"
              :label="$t('PurchaseTasks.Fields.created')"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="purchase_plan_code"
                  v-model="options.purchase_plan_code"
                  @text="purchasePlanCode = $event"
                  :label="$t('PurchaseTasks.Fields.purchase_plan_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="purchase_task_code"
                  v-model="options.purchase_task_code"
                  @text="purchaseTaskCode = $event"
                  :label="$t('PurchaseTasks.Fields.purchase_task_code')"
                />
              </v-col>
              <!-- 仓库 -->
              <v-col cols="12" sm="6" md="4">
                <SearcherStocks :stockTypes="[0]" v-model="stocksSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherAccountUsers
                  v-model="accountUsersSelected"
                  :type="[2]"
                  :label="$t('PurchaseTasks.Fields.purchase_user')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.status"
                  :label="$t('PurchaseTasks.status.title')"
                  :items="$consts.purchaseTasks.status"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="mx-4 my-4">
        <v-data-table
          id="purchase_from_order_table"
          :headers="headers"
          class="elevation-2 pa-4 mt-4"
          :items="purchaseTasks"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :footer-props="$t('$config.footer-props')"
          :loading="loading"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | purchaseTasksStatusColor"
              :text-color="item.status | purchaseTasksStatusTextColor"
            >{{ item.status | purchaseTasksStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.distribute_task_date="{ item }">
            {{ item.distribute_task_date | formatDate }}
          </template>
          <template v-slot:item.purchase_user="{ item }">
            {{ item.purchase_user ? item.purchase_user.name : '' }}
          </template>
          <template v-slot:item.created="{ item }">
            {{ item.created | formatTime }}
          </template>
          <template v-slot:item.actions="{ item }">
            <PurchaseTasksActions
              :purchaseTasks="item"
              showView
              :showComplete="item.status == 1"
            />
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import PurchaseTasks from "@/api/purchase_tasks";
import PurchaseTasksActions from "./plugins/Actions"
import SearcherDatetimeRange from "@/components/plugins/SearcherDatetimeRange";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStocks from "@/components/plugins/SearcherStocks";
import SearcherAccountUsers from "@/components/plugins/SearcherAccountUsers";
import SearcherStatus from "@/components/plugins/SearcherStatus"

const defaultOptions = {
  created_time_from:
    new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  purchase_plan_code: "",
  purchase_task_code: "",
  purchase_user_id: [],
  stock_ids: [],
  status: [1, 2]
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    PurchaseTasksActions,
    SearcherDatetimeRange,
    SearcherKeyword,
    SearcherStocks,
    SearcherAccountUsers,
    SearcherStatus,
  },

  computed: {
    items() {
      let items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.purchases"),
          disabled: false,
          href: "/purchases",
        },
        {
          text: i18n.t("Nav.purchaseTasks"),
          disabled: true,
          href: "/purchase-tasks",
        },
      ];
      return items;
    },

    headers() {
      let headers = [
        {
          text: i18n.t("PurchaseTasks.Fields.purchase_task_code"),
          align: "start",
          sortable: false,
          value: "purchase_task_code",
        },
        {
          text: i18n.t("PurchaseTasks.Fields.purchase_plan_code"),
          align: "start",
          sortable: false,
          value: "purchase_plan.purchase_plan_code",
        },
        {
          text: i18n.t("PurchaseTasks.Fields.stock_name"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "stock.name",
        },
        {
          text: i18n.t("PurchaseTasks.Fields.purchase_user"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "purchase_user",
        },
        {
          text: i18n.t("PurchaseTasks.Fields.status"),
          align: "start",
          value: "status",
        },
        {
          text: i18n.t("PurchaseTasks.Fields.memo"),
          align: "start",
          sortable: false,
          value: "memo",
        },
        {
          text: i18n.t("PurchaseTasks.Fields.created"),
          align: "start",
          value: "created",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        }
      ];
      return headers;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["settings"]),
  },

  data: () => ({
    purchaseTasks: [],
    paging: {},
    options: JSON.parse(
      sessionStorage.getItem("purchase-tasks-options")
        ? sessionStorage.getItem("purchase-tasks-options")
        : JSON.stringify(defaultOptions)
    ),
    datetimeRage: [
      new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
        .toISOString()
        .substr(0, 10) + " 00:00:00",
      new Date().toISOString().substr(0, 10) + " 23:59:59",
    ],
    purchasePlanCode: "",
    purchaseTaskCode: "",
    stocksSelected: [],
    accountUsersSelected: [],
    loading: false,
  }),

  mounted() {
    this.$commonUtils
      .loadOptions("purchase-tasks", ["stocksSelected", "accountUsersSelected"])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
            if (
              typeof this.options.created_time_from != "undefined" &&
              typeof this.options.created_time_to != "undefined"
            )
              this.datetimeRage = [
                this.options.created_time_from,
                this.options.created_time_to,
              ];
          } else this.resetSearch();
          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "stocksSelected":
                this.stocksSelected = selected.value;
                break;
              case "accountUsersSelected":
                this.accountUsersSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    resetSearch() {
      this.purchasePlanCode = "";
      this.purchaseTaskCode = "";
      this.stocksSelected = [];
      this.accountUsersSelected = [];
      this.datetimeRage = [
        new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
          .toISOString()
          .substr(0, 10) + " 00:00:00",
        new Date().toISOString().substr(0, 10) + " 23:59:59",
      ];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.purchase_plan_code, this.$refs.purchase_task_code].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        purchase_plan_code: this.purchasePlanCode,
        purchase_task_code: this.purchaseTaskCode,
      });
    },
    getPurchaseTasksFromAPI() {
      this.loading = true;
      PurchaseTasks.fetch(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.purchaseTasks = response.data.purchase_tasks;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getPurchaseTasksFromAPI();
        sessionStorage.setItem(
          "purchase-tasks-options",
          JSON.stringify(this.options)
        );
      },
    },
    stocksSelected: {
      handler() {
        this.options.stock_ids = this.stocksSelected
          ? this.stocksSelected.map((e) => {
            return e.id
          })
          : [];
        sessionStorage.setItem(
          "purchase-tasks-stocksSelected",
          JSON.stringify(this.stocksSelected)
        );
      },
    },
    accountUsersSelected: {
      handler() {
        this.options.purchase_user_id = this.accountUsersSelected
        ? this.accountUsersSelected.map((e) => {
          return e.id
        })
        : [];
        sessionStorage.setItem(
          "purchase-tasks-accountUsersSelected",
          JSON.stringify(this.accountUsersSelected)
        );
      }
    },
    datetimeRage: {
      handler() {
        if (this.datetimeRage[0] == null || this.datetimeRage[0] == "") {
          this.options.created_time_from =
            new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 00:00:00";
        } else {
          this.options.created_time_from = this.datetimeRage[0];
        }

        if (this.datetimeRage[1] == null || this.datetimeRage[1] == "") {
          this.options.created_time_to = new Date().toISOString().substr(0, 10) + " 23:59:59";
        } else {
          this.options.created_time_to = this.datetimeRage[1];
        }
      },
    },
  },
};
</script>